/**
 * Detects if a string looks like HTML content.
 * This is used to auto-detect when content should use "naked" mode
 * instead of markdown/fancy mode.
 */
export function looksLikeHtml(content: string): boolean {
  if (!content || !content.trim()) {
    return false;
  }

  // Short-circuit if content explicitly marks itself as fancy mode
  if (
    content.trimStart().startsWith("<!-- buttondown-editor-mode: fancy -->")
  ) {
    return false;
  }

  if (
    content.trimStart().startsWith("<!-- buttondown-editor-mode: plaintext -->")
  ) {
    return false;
  }

  // Check for common HTML patterns:
  // 1. HTML tags with attributes (e.g., <div class="...">, <p style="...">)
  // 2. Self-closing tags (e.g., <br/>, <img src="..."/>)
  // 3. HTML closing tags (e.g., </div>, </p>)
  // 4. Multiple HTML tags indicating structure

  const htmlTagWithAttributesPattern =
    /<[a-z][^>]*(?:style|class|id|href|src|alt|width|height|data-)=["'][^"']*["'][^>]*>/i;
  const selfClosingTagPattern = /<[a-z]+\s+[^>]*\/>/i;
  const closingTagPattern = /<\/[a-z]+>/i;
  // const htmlTagPattern = /<[a-z][\s\S]*?>/i;

  // Count HTML tags
  const tagMatches = content.match(/<[a-z][\s\S]*?>/gi);
  const tagCount = tagMatches ? tagMatches.length : 0;

  // Strong indicators of HTML:
  // 1. Tags with attributes (very unlikely in markdown)
  if (htmlTagWithAttributesPattern.test(content)) {
    return true;
  }

  // 2. Self-closing tags
  if (selfClosingTagPattern.test(content)) {
    return true;
  }

  // 3. Closing tags (markdown doesn't use closing tags)
  if (closingTagPattern.test(content)) {
    return true;
  }

  // 4. Multiple HTML tags (indicates HTML structure)
  if (tagCount > 2) {
    return true;
  }

  // 5. Common HTML tags that aren't typically in markdown
  const commonHtmlTags =
    /<(div|span|section|article|header|footer|nav|aside|table|thead|tbody|tr|td|th|ul|ol|li|dl|dt|dd|form|input|button|select|textarea|label|fieldset|legend|meta|link|script|style)[\s>]/i;
  if (commonHtmlTags.test(content)) {
    return true;
  }

  return false;
}
